import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import ArcResults from '../components/ArcResults';
import { ENGINES } from '../mock/mock';
import { ArrowRight, Loader2 } from 'lucide-react';
import { getBackendUrl } from '../lib/backendUrl';

const API = getBackendUrl();

export default function SharedArc() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/share/${encodeURIComponent(token)}`);
        if (!r.ok) throw new Error(r.status === 404 ? 'This Arc share has expired or never existed.' : 'Could not load this Arc.');
        const d = await r.json();
        if (!cancel) setData(d);
      } catch (e) {
        if (!cancel) setErr(e.message);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050403]">
        <div className="flex items-center gap-2 text-white/55 font-data text-[12px] tracking-[0.18em] uppercase">
          <Loader2 size={14} className="animate-spin" /> Calibrating…
        </div>
      </div>
    );
  }
  if (err || !data || !data.arcKey) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050403] px-6">
        <div className="text-center max-w-[480px]">
          <div className="font-data text-[11px] tracking-[0.32em] uppercase text-white/45 mb-2">404 · Not found</div>
          <h1 className="font-head text-[28px] text-white">{err || 'Share not found.'}</h1>
          <Link to="/" className="inline-flex items-center gap-2 mt-6 px-6 py-3 text-[12px] tracking-[0.22em] uppercase font-bold text-[#0B0A08]"
                style={{ background: 'linear-gradient(135deg,#C7A46D,#B5925F)', clipPath: 'polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%)' }}>
            Find your Arc <ArrowRight size={13}/>
          </Link>
        </div>
      </div>
    );
  }

  const engine = ENGINES[data.top] || ENGINES.TITAN;
  const isFull = Boolean(data.isFull);
  const accuracy = typeof data.accuracy === 'number'
    ? data.accuracy
    : (isFull ? 94 : null);
  const result = {
    top: data.top, second: data.second, third: data.third,
    scores: data.scores || {}, enginePct: data.enginePct || {},
    sorted: Object.entries(data.scores || {}).sort((a, b) => b[1] - a[1]),
    arcKey: data.arcKey,
    accuracy: accuracy ?? 0,
    isFull,
  };

  return (
    <div className="min-h-screen bg-[#050403]">
      <div className="myarc-bg" />
      <div className="relative z-10">
        <header className="border-b border-white/6 bg-[#050403]/85 backdrop-blur-xl sticky top-0 z-30">
          <div className="max-w-[1180px] mx-auto px-5 md:px-8 py-4 flex items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2">
              <img src="/assets/logo.png" alt="" className="h-6 w-auto" />
              <span className="font-logo text-[15px] tracking-[0.28em]">MYARC</span>
            </Link>
            <div className="font-data text-[10px] tracking-[0.22em] uppercase text-white/50">
              {data.displayName ? `Shared by ${data.displayName}` : 'Public Arc'}
            </div>
            <Link to="/assessment"
                  className="hidden md:inline-flex items-center gap-2 px-5 py-2 text-[11px] tracking-[0.22em] uppercase font-bold text-[#0B0A08]"
                  style={{ background: 'linear-gradient(135deg,#C7A46D,#B5925F)', clipPath: 'polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%)' }}>
              Find yours <ArrowRight size={12}/>
            </Link>
          </div>
        </header>
        <div className="px-5 md:px-8 py-6">
          <div className="max-w-[1180px] mx-auto mb-3">
            <div className="font-data text-[10px] tracking-[0.22em] uppercase text-white/45">Public Arc · read only</div>
          </div>
          <ArcResults
            result={result}
            onContinueDeeper={null}
            onEnter={null}
            readOnly
            ownerName={data.displayName}
          />
          <div className="max-w-[1180px] mx-auto mt-10 text-center">
            <div className="inline-flex flex-col items-center gap-3 px-7 py-7 rounded-2xl"
                 style={{ background: `radial-gradient(circle at center, ${engine.primary}18, transparent 70%)`, border: `1px solid ${engine.primary}40` }}>
              <div className="font-data text-[10px] tracking-[0.32em] uppercase" style={{ color: engine.primary }}>This is one of 30 Arcs</div>
              <h2 className="font-head text-[26px] md:text-[34px] text-white">Find your own.</h2>
              <p className="text-white/65 max-w-[480px] text-[14px] leading-[1.7]">A 60-question diagnostic that surfaces the engine actually driving your decisions and a specific operating system to build from it.</p>
              <Link to="/assessment"
                    className="mt-2 inline-flex items-center gap-2 px-7 py-3 text-[12px] tracking-[0.22em] uppercase font-bold text-[#0B0A08]"
                    style={{ background: 'linear-gradient(135deg,#C7A46D,#B5925F)', clipPath: 'polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%)' }}>
                Take the Brain Scan <ArrowRight size={13}/>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
